// src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadUserProfile, saveUserProfile } from '../services/storageService';

type ThemeType = 'dark' | 'light' | 'system';

interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  setTheme: () => {},
  isDarkMode: true
});

export const ThemeProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeType>('dark');
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  // Function to apply theme to document.
  // Drives the redesign token classes (theme-dark | theme-light | theme-auto)
  // on <html>, and keeps the Tailwind `dark` class in sync for backward compat.
  const applyTheme = (newTheme: ThemeType) => {
    let shouldBeDark = true;

    if (newTheme === 'system') {
      shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else {
      shouldBeDark = newTheme === 'dark';
    }

    const root = document.documentElement;
    // 'system' maps to the token class `theme-auto` which follows the OS via CSS.
    const themeClass =
      newTheme === 'system'
        ? 'theme-auto'
        : newTheme === 'light'
        ? 'theme-light'
        : 'theme-dark';
    root.classList.remove('theme-dark', 'theme-light', 'theme-auto');
    root.classList.add(themeClass);

    // Tailwind `dark:` utilities still work during the migration.
    root.classList.toggle('dark', shouldBeDark);
    setIsDarkMode(shouldBeDark);
  };
  
  useEffect(() => {
    // Load theme from user profile
    const profile = loadUserProfile();
    const savedTheme = profile.preferences.theme;
    setThemeState(savedTheme);
    applyTheme(savedTheme);
    
    // Listen for system theme changes if using system theme
    if (savedTheme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      
      // Add event listener with backward compatibility
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } else {
        // For older browsers
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, []);
  
  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    
    // Update user profile
    const profile = loadUserProfile();
    saveUserProfile({
      preferences: {
        ...profile.preferences,
        theme: newTheme
      }
    });
    
    // Apply theme to document
    applyTheme(newTheme);
  };
  
  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);